import {
  Alert,
  Box,
  Center,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { z } from 'zod';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { StudentUserDetail } from './StudentUserDetail';
import { StudentActionsMenu } from './StudentActionsMenu';
import { StudentDeletedAlert } from './StudentDeletedAlert';
import { DeletedBadge } from '@duke-hq/ui/components/DeletedBadge';
import { testIds } from './testIds';
import { getUserFullName, constructQueryParams } from '@duke-hq/lib';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { StudentEnrolments } from './StudentEnrolments';
import { StudentAuditLogs } from './StudentAuditLogs';
import { SupportCaseload } from './SupportCaseload';
import { useMemo, useCallback } from 'react';
import { SupportStudent } from './SupportStudent';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { IdCard } from 'lucide-react';
import { SortOrderSchema } from '@duke-hq/lib/pagination';
import { NoteList } from '../Notes';
import { StudentContactsList } from '../SupportTasks/StudentContactsList';
import { StudentDevices } from './StudentDevices';
import { StudentDocuments } from './StudentDocuments';
import { useCheckPermission } from '../hooks/useCheckPermission';
import { LoginAsButton } from '../components/LoginAsButton';
export type StudentType = RouterOutputs['students']['byId'];

const SortFiltersSchema = z.object({
  orderBy: z.string().optional(),
  sort: SortOrderSchema.optional(),
  status: z.string().optional(),
  provider: z.string().optional(),
  page: z.string().optional(),
});

export function StudentDetailPage() {
  const { studentId, tab, value } = useParams<{
    studentId: string;
    tab: string;
    value: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const canManageStudentDocuments = useCheckPermission('canManageStudentDocuments');

  const tabs = useMemo(
    () =>
      [
        { value: 'enrolments', label: t`Enrolments` },
        { value: 'contacts', label: t`Contacts` },
        { value: 'chats', label: t`Student Chat` },
        { value: 'support', label: t`Consultant Chat` },
        { value: 'notes', label: t`Notes` },
        ...(canManageStudentDocuments
          ? ([{ value: 'documents', label: t`Documents` }] as const)
          : ([] as const)),
        { value: 'audit-logs', label: t`Audit Logs` },
        { value: 'details', label: t`Personal details` },
        { value: 'devices', label: t`Devices` },
      ] as const,
    [canManageStudentDocuments],
  );

  const selectedTab = useMemo(() => {
    if (typeof tab !== 'string') {
      return 0;
    }

    const tabIdx = tabs.findIndex((tabObj) => tabObj.value === tab);
    return tabIdx !== -1 ? tabIdx : 0;
  }, [tab, tabs]);

  const getPreserveUrl = useMemo(() => {
    const parsedData = SortFiltersSchema.safeParse({
      status: searchParams.get('status') ?? undefined,
      provider: searchParams.get('provider') ?? undefined,
      sort: searchParams.get('sort') ?? undefined,
      orderBy: searchParams.get('orderBy') ?? undefined,
      page: searchParams.get('page') ?? undefined,
    });

    if (!parsedData.success || parsedData.data == null) {
      return '';
    }

    const { status, provider, sort, orderBy, page } = parsedData.data;
    const search = searchParams.get('search') ?? undefined;
    return constructQueryParams({ status, provider, sort, orderBy, page, search });
  }, [searchParams]);

  const handleTabsChange = useCallback(
    (index: number) => {
      if (typeof tabs[index] !== 'undefined') {
        navigate(`/student/${studentId}/${tabs[index].value}${getPreserveUrl}`);
      }
    },
    [getPreserveUrl, navigate, studentId, tabs],
  );

  const { data, isLoading } = trpcAdmin.students.byId.useQuery(
    { id: studentId ?? '' },
    {
      enabled: Boolean(studentId),
    },
  );

  if (!studentId) {
    return null;
  }

  if (isLoading || !data) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!data) {
    return (
      <Alert>
        <Trans>No student found</Trans>
      </Alert>
    );
  }

  return (
    <Flex flexDirection="column" w="100%" h="100%">
      <DrawerHeader
        borderBottomWidth="1px"
        borderBottomColor="border.default"
        p={{ base: 2, md: 4 }}
      >
        <Flex align="center" justifyContent="space-between" gap={3}>
          <HStack align="center" spacing="3">
            <UserAvatar
              user={{
                first_name: data?.first_name,
                last_name: data?.last_name,
                picture_url: data?.picture_url,
              }}
              size="lg"
            />
            <VStack align="start" spacing={0}>
              <HStack align="center" spacing={2}>
                <Text fontWeight="bold">
                  {getUserFullName({
                    first_name: data?.first_name,
                    last_name: data?.last_name,
                    picture_url: data?.picture_url,
                  })}
                </Text>
                <LoginAsButton kind="student" studentId={data.id} />
                {data.deleted_at != null ? (
                  <DeletedBadge data-testid={testIds.deletedStudentHeaderBadge}>
                    <Trans>Deleted</Trans>
                  </DeletedBadge>
                ) : null}
              </HStack>
              {data?.studentData?.universal_student_identifier ? (
                <HStack align="center" gap="1" fontSize="sm" color="fg.muted">
                  <Icon as={IdCard} color="fg.subtle" boxSize="5" />
                  <Text>{data?.studentData?.universal_student_identifier}</Text>
                </HStack>
              ) : null}
            </VStack>
          </HStack>

          <Box marginRight={12} marginTop={-5}>
            <StudentActionsMenu student={data} />
          </Box>
        </Flex>
      </DrawerHeader>

      <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
        <Tabs
          isLazy
          flexDirection="column"
          display="flex"
          w="100%"
          h="100%"
          index={selectedTab}
          onChange={handleTabsChange}
        >
          <TabList
            bg="bg.subtle"
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                bg={selectedTab === index ? 'bg.surface' : ''}
                _hover={{ bg: 'bg.muted' }}
                flexShrink={0}
                whiteSpace="nowrap"
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels width="100%" height="100%" overflow="auto">
            {tabs.map((tab, index) => (
              <TabPanel key={index} width="100%" height="100%" overflow="auto" p={0}>
                {tab.value === 'enrolments' && <StudentEnrolments student={data} />}
                {tab.value === 'contacts' && data.drn ? (
                  <StudentContactsList studentDrn={data.drn} />
                ) : null}
                {tab.value === 'chats' && (
                  <SupportStudent
                    studentDrn={data.drn}
                    selectedChat={selectedTab === index ? value : undefined}
                  />
                )}
                {tab.value === 'support' &&
                  (data.providerDrn ? (
                    <SupportCaseload
                      studentDrn={data.drn}
                      providerDrn={data.providerDrn}
                      consultantDrn={data.consultantDrn}
                      selectedChat={selectedTab === index ? value : undefined}
                    />
                  ) : (
                    <Trans>Student is not added to any provider</Trans>
                  ))}
                {tab.value === 'notes' && data.providerId != null ? (
                  <NoteList tenantId={data.providerId} userDrn={data.notesDrn} />
                ) : null}
                {tab.value === 'documents' && data.providerId != null ? (
                  <StudentDocuments student={data} />
                ) : null}
                {tab.value === 'audit-logs' && <StudentAuditLogs student={data} />}
                {tab.value === 'details' && (
                  <>
                    <StudentDeletedAlert student={data} />
                    <StudentUserDetail student={data} />
                  </>
                )}
                {tab.value === 'devices' && <StudentDevices student={data} />}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </DrawerBody>
    </Flex>
  );
}
